import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { eventStore, useEventStore } from "../../../../store/event";
import { useOrganizationStore } from "../../../../store/organization";
import { GET_FINANCIAL_DAILY, GET_PRODUCT_MIX, GET_COMMISSARY_ORDERS, GET_VENDORS, GET_EVENT, GET_PAYOUT_ADJUSTMENTS } from "./queries";
import { INSERT_PAYOUT_ADJUSTMENT, DELETE_PAYOUT_ADJUSTMENT } from "./mutations";
import { processData } from "./helpers";
import { VendorLocationData, FinancialRow, ProductMixRow, CommissaryOrder, VendorConfig, PayoutAdjustment } from "./types";
import { EventStatsBar } from "./EventStatsBar";
import { VendorCard, groupByVendor } from "./VendorCard";
import { ReportFilters } from "../../components/ReportFilters";
import { SecondaryButton } from "../../../../components/Button";
import { GET_LIST_EVENTS_BY_ORGANIZATION } from "../../../../queries/event";
import { exportPayoutExcel } from "./exportExcel";
import { VendorPayoutPDF } from "./VendorPayoutPDF";
import { pdf } from "@react-pdf/renderer";

const client = createApolloClient();
const { getState: getOrgState } = organizationStore;
const { getState: getEventState } = eventStore;

export const VendorPayouts = () => {
  const location = useLocation();
  const currentOrgId = useOrganizationStore((state) => state.organizationId);
  const currentEventId = useEventStore((state) => state.eventId);

  const [vendorData, setVendorData] = useState<VendorLocationData[]>([]);
  const [organizerLogoUrl, setOrganizerLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const vendorGroups = useMemo(() => groupByVendor(vendorData), [vendorData]);
  const eventStats = useMemo(() => {
    return vendorGroups.reduce(
      (acc, g) => ({
        totalRevenue: acc.totalRevenue + g.totalGross,
        totalTax: acc.totalTax + g.totalTax,
        totalPayoutsDue: acc.totalPayoutsDue + g.totalDue,
        totalDeductions: acc.totalDeductions + g.totalDeductions + g.totalAdjustments,
        totalAdjustments: 0,
        vendorLocationCount: acc.vendorLocationCount + g.locations.length,
      }),
      { totalRevenue: 0, totalTax: 0, totalPayoutsDue: 0, totalDeductions: 0, totalAdjustments: 0, vendorLocationCount: 0 }
    );
  }, [vendorGroups]);
  const eventName = vendorData[0]?.eventName ?? "";

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { eventId } = getEventState();
      const organizationId = getOrgState().organizationId;

      // Parse URL where from ReportFilters
      const search = new URLSearchParams(location.search);
      const whereParam = search.get("where");
      let financialWhere: any = whereParam ? JSON.parse(whereParam) : {};

      if (eventId) {
        financialWhere.event_id = { _eq: eventId };

        // Fetch event for organizer logo only — date scoping is user-controlled via ReportFilters.
        const { data: eventData } = await client.query({
          query: GET_EVENT,
          variables: { id: eventId },
          fetchPolicy: "cache-first",
        });
        setOrganizerLogoUrl(eventData?.events_by_pk?.organization?.logo_url ?? null);
      } else if (organizationId) {
        const { data: eventsData } = await client.query({
          query: GET_LIST_EVENTS_BY_ORGANIZATION,
          variables: { where: { organization_id: { _eq: organizationId } } },
          fetchPolicy: "network-only",
        });
        const eventIds = (eventsData?.events || []).map((e: any) => e.id);
        if (eventIds.length > 0) {
          financialWhere.event_id = { _in: eventIds };
        }
      }

      // Build date where for product mix (same date filtering)
      const dateWhere: any = { ...financialWhere };

      // Commissary where: event-scoped only (no organization_id)
      const commissaryWhere: any = {};
      if (financialWhere.event_id) {
        commissaryWhere.event_id = financialWhere.event_id;
      }
      commissaryWhere.status = { _eq: "fulfilled" };

      // Payout adjustments where: event-scoped (mirrors commissary)
      const adjustmentsWhere: any = {};
      if (financialWhere.event_id) {
        adjustmentsWhere.event_id = financialWhere.event_id;
      }

      // Vendors where
      const vendorsWhere: any = {};
      if (organizationId) {
        vendorsWhere.organization_id = { _eq: organizationId };
      }

      // Fetch all data in parallel
      const [financialRes, productMixRes, commissaryRes, vendorsRes, adjustmentsRes] = await Promise.all([
        client.query({
          query: GET_FINANCIAL_DAILY,
          variables: { where: financialWhere },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: GET_PRODUCT_MIX,
          variables: { where: dateWhere },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: GET_COMMISSARY_ORDERS,
          variables: { where: commissaryWhere },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: GET_VENDORS,
          variables: { where: vendorsWhere },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: GET_PAYOUT_ADJUSTMENTS,
          variables: { where: adjustmentsWhere },
          fetchPolicy: "network-only",
        }),
      ]);

      const financialData: FinancialRow[] = financialRes.data?.reports_financial || [];
      const productMixData: ProductMixRow[] = productMixRes.data?.reports_product_mix || [];
      const commissaryOrders: CommissaryOrder[] = commissaryRes.data?.order_commissary || [];
      const vendorConfigs: VendorConfig[] = vendorsRes.data?.vendors || [];
      const payoutAdjustments: PayoutAdjustment[] = adjustmentsRes.data?.vendor_payout_adjustments || [];

      const processed = processData(financialData, productMixData, commissaryOrders, payoutAdjustments, vendorConfigs, null, null);
      if (!mountedRef.current) return;
      setVendorData(processed);
      setHasGenerated(true);
    } catch (err: any) {
      console.error("Error fetching vendor payouts:", err);
      if (!mountedRef.current) return;
      setError(err.message || "Failed to load data");
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [location.search]);

  const canGenerate = !!currentOrgId && !!currentEventId;

  const insertEntry = async (
    vendorId: number,
    kind: "deduction" | "credit",
    description: string,
    amountCents: number
  ) => {
    const { eventId } = getEventState();
    if (!eventId) return;

    await client.mutate({
      mutation: INSERT_PAYOUT_ADJUSTMENT,
      variables: {
        object: {
          event_id: eventId,
          vendor_id: vendorId,
          kind,
          amount: Math.abs(amountCents),
          description,
        },
      },
    });

    await fetchData();
  };

  const handleAddDeduction = (vendorId: number, description: string, amountCents: number) =>
    insertEntry(vendorId, "deduction", description, amountCents);

  const handleAddAdjustment = (vendorId: number, description: string, amountCents: number) =>
    insertEntry(vendorId, "credit", description, amountCents);

  const handleDeleteAdjustment = async (id: number) => {
    await client.mutate({
      mutation: DELETE_PAYOUT_ADJUSTMENT,
      variables: { id },
    });

    await fetchData();
  };

  const toggleVendor = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Filters + Generate */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 pb-3 mb-4">
        <ReportFilters
          includeVendors
          eventScopedVendors
          includeDates
          includeDateOnly
          includeDatesLast90Days
          customQueryPaths={{
            dateFrom: "_and[0].transaction_date",
            dateTo: "_and[1].transaction_date",
          }}
        />
        <div className="flex items-center justify-between pt-2">
          <div>
            {!canGenerate && (
              <span className="text-xs text-gray-400">Select an organization and event to generate the report.</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasGenerated && (
              <>
                <SecondaryButton onClick={() => exportPayoutExcel(vendorGroups, { eventName })}>
                  Export Excel
                </SecondaryButton>
                <SecondaryButton onClick={async () => {
                  const doc = <VendorPayoutPDF vendorGroups={vendorGroups} filters={{ eventName, organizerLogoUrl }} />;
                  const blob = await pdf(doc).toBlob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  const timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
                  a.download = `Vendor_Payouts_${timestamp}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}>
                  Export PDF
                </SecondaryButton>
              </>
            )}
            <button
              onClick={fetchData}
              disabled={!canGenerate || isLoading}
              className={`px-4 py-2 text-sm font-semibold rounded-lg shadow-sm transition-colors ${
                canGenerate && !isLoading
                  ? "bg-red-700 text-white hover:bg-red-800"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {isLoading ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </div>
      </div>

      {/* Event Stats — only show after generating */}
      {hasGenerated && <EventStatsBar stats={eventStats} eventName={eventName} />}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-red-700" />
        </div>
      )}

      {/* Empty states */}
      {!isLoading && !hasGenerated && !error && (
        <div className="text-center py-20 text-gray-400">
          Select an organization and event, then click Generate Report.
        </div>
      )}

      {!isLoading && hasGenerated && vendorData.length === 0 && !error && (
        <div className="text-center py-20 text-gray-500">
          No vendor data found for this event.
        </div>
      )}

      {!isLoading && vendorGroups.length > 0 && (
        <div className="space-y-3">
          {vendorGroups.map((group) => (
            <VendorCard
              key={group.vendorId}
              group={group}
              expandedLocationKey={expandedKey}
              onToggleLocation={toggleVendor}
              onAddAdjustment={handleAddAdjustment}
              onAddDeduction={handleAddDeduction}
              onDeleteAdjustment={handleDeleteAdjustment}
            />
          ))}
        </div>
      )}
    </div>
  );
};
